import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../../hooks/useNotification';
// import { useMediaPermissions } from '../../hooks/useMediaPermissions';
import { logger } from '../../utils/logger';

interface NotificationPermissionProps {
  onPermissionGranted?: () => void;
  onPermissionDenied?: () => void;
  className?: string;
}

export const NotificationPermission: React.FC<NotificationPermissionProps> = ({
  onPermissionGranted,
  onPermissionDenied,
  // className = '',
}) => {
  const { t } = useTranslation();
  const { permission, isSupported, requestPermission, showNotification } = useNotification();
  // const { requestPhotoLibraryAccess } = useMediaPermissions();
  const [isRequesting, setIsRequesting] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [currentStep, setCurrentStep] = useState<'notification' | 'complete'>('notification');
  const [isPWAInstalled, setIsPWAInstalled] = useState(false);

  useEffect(() => {
    // Check if PWA is installed
    const checkPWAInstalled = () => {
      const standalone = window.matchMedia('(display-mode: standalone)').matches;
      const isFullscreen = window.matchMedia('(display-mode: fullscreen)').matches;
      const isMinimalUI = window.matchMedia('(display-mode: minimal-ui)').matches;
      const isIOSStandalone =
        (window.navigator as Navigator & { standalone?: boolean }).standalone === true;

      setIsPWAInstalled(standalone || isFullscreen || isMinimalUI || isIOSStandalone);
    };

    checkPWAInstalled();
  }, []);

  useEffect(() => {
    // Check how many times user has dismissed the prompt
    const dismissCount = parseInt(
      localStorage.getItem('notification-permission-dismiss-count') || '0',
    );
    const maxDismissals = 3; // Allow up to 3 dismissals

    // Check if user has already granted notification permission and completed the flow
    const hasCompletedFlow = localStorage.getItem('notification-flow-completed') === 'true';

    // Show prompt if notifications are supported, permission is default, and user hasn't exceeded dismiss limit
    // Also show if permission is granted but we're still in the multi-step flow (media permissions)
    // For non-PWA users, only show notification permission (skip media permissions)
    logger.log(
      'NotificationPermission - isSupported:',
      isSupported,
      'permission:',
      permission,
      'dismissCount:',
      dismissCount,
      'maxDismissals:',
      maxDismissals,
      'currentStep:',
      currentStep,
      'isPWAInstalled:',
      isPWAInstalled,
      'hasCompletedFlow:',
      hasCompletedFlow,
    );

    // Don't show if user has already completed the flow
    if (hasCompletedFlow) {
      setShowPrompt(false);
      return;
    }

    // If granted, mark flow complete and avoid showing again
    if (permission === 'granted') {
      localStorage.setItem('notification-flow-completed', 'true');
    }

    const shouldShowPrompt =
      isSupported && permission === 'default' && dismissCount < maxDismissals && !hasCompletedFlow;

    if (shouldShowPrompt) {
      setShowPrompt(true);
      logger.log('NotificationPermission - Showing permission prompt');
    } else {
      setShowPrompt(false);
      if (dismissCount >= maxDismissals) {
        logger.log('NotificationPermission - Max dismissals reached, not showing prompt');
      } else if (permission === 'granted') {
        logger.log('NotificationPermission - Permission granted, hiding prompt');
      } else if (hasCompletedFlow) {
        logger.log('NotificationPermission - Flow already completed, hiding prompt');
      } else {
        logger.log('NotificationPermission - Hiding permission prompt');
      }
    }
  }, [isSupported, permission, currentStep, isPWAInstalled]);

  const handleRequestPermission = async () => {
    if (!isSupported) {
      logger.warn('Notifications not supported');
      setError(t('notification_not_supported', 'Notifications are not supported on this device'));
      return;
    }

    setIsRequesting(true);
    setError(null);
    try {
      if (currentStep === 'notification') {
        const result = await requestPermission();

        if (result === 'granted') {
          // Complete the flow directly after notification permission
          setCurrentStep('complete');
          localStorage.setItem('notification-flow-completed', 'true');
          logger.log('Notification permission granted, completing flow');

          // Test notification ပေးပို့်မယ် - delay ထည့်ပြီး browser blocking ကို ရှောင်မယ်
          setTimeout(async () => {
            try {
              logger.log('Sending test notification after permission granted');
              const testNotification = await showNotification({
                title: t('test_notification_title', 'Notification Enabled'),
                body: t(
                  'test_notification_body',
                  'Notifications are now enabled! You will receive updates about your payments.',
                ),
                icon: '/icon-192x192.svg',
                tag: 'test-notification',
                requireInteraction: false,
                silent: false,
              });

              if (testNotification) {
                logger.log('Test notification sent successfully:', testNotification);

                // Auto close notification after 5 seconds
                setTimeout(() => {
                  testNotification.close();
                  logger.log('Test notification closed automatically');
                }, 5000);
              } else {
                logger.warn('Test notification was not created');
              }
            } catch (notificationError) {
              logger.error('Error sending test notification:', notificationError);
            }
          }, 1000); // 1 second delay
        } else {
          onPermissionDenied?.();
          logger.warn('Notification permission denied');
        }
      } else if (currentStep === 'complete') {
        // This step should not be reached in the UI, but handle it gracefully
        setShowPrompt(false);
        localStorage.setItem('notification-flow-completed', 'true');
        onPermissionGranted?.();
      }
    } catch (error) {
      logger.error('Error requesting notification permission:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      if (errorMessage.includes('NotAllowedError') || errorMessage.includes('denied')) {
        setError(
          t(
            'notification_permission_denied',
            'Notification permission was denied. You can enable it later in your browser settings.',
          ),
        );
      } else if (errorMessage.includes('AbortError')) {
        setError(
          t(
            'notification_request_cancelled',
            'Permission request was cancelled. Please try again.',
          ),
        );
      } else if (errorMessage.includes('NotSupportedError')) {
        setError(t('notification_not_supported', 'Notifications are not supported on this device'));
      } else {
        setError(
          t(
            'notification_error_generic',
            'Failed to request notification permission. Please try again or check your browser settings.',
          ),
        );
      }

      setRetryCount((prev) => prev + 1);
      onPermissionDenied?.();
    } finally {
      setIsRequesting(false);
    }
  };

  const handleDismiss = () => {
    const currentDismissCount = parseInt(
      localStorage.getItem('notification-permission-dismiss-count') || '0',
    );
    const newCount = currentDismissCount + 1;
    localStorage.setItem('notification-permission-dismiss-count', newCount.toString());

    setShowPrompt(false);
    logger.log('NotificationPermission - User dismissed permission request');

    if (newCount >= 3) {
      logger.log('NotificationPermission - Max dismissals reached, will not show again');
    }
  };

  if (!isSupported) {
    return null;
  }

  // Hide for users with granted permission, or when prompt should not be shown
  if (permission === 'granted' || !showPrompt) {
    return null;
  }

  const getStepTitle = () => {
    switch (currentStep) {
      case 'notification':
        return t('enable_notifications', 'Enable Notifications');
      default:
        return t('setup_complete', 'Setup Complete');
    }
  };

  const getStepDescription = () => {
    switch (currentStep) {
      case 'notification':
        return t(
          'notification_permission_description',
          'Get notified about payment updates, transaction status, and important account information.',
        );
      default:
        return t(
          'setup_complete_description',
          'All permissions have been configured successfully.',
        );
    }
  };

  const getStepIcon = () => {
    switch (currentStep) {
      case 'notification':
        return '🔔';
      default:
        return '✅';
    }
  };

  const getButtonText = () => {
    switch (currentStep) {
      case 'notification':
        return t('enable_notifications_button', 'Enable');
      default:
        return t('complete_button', 'Complete');
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white/95 backdrop-blur-md border border-white/20 shadow-2xl rounded-2xl p-4 sm:p-6 w-full max-w-md mx-4">
        <div className="text-center">
          {/* Header */}
          <div className="mb-6">
            <div className="mb-3 text-4xl">{getStepIcon()}</div>
            <h2 className="mb-2 text-xl font-semibold text-gray-900">{getStepTitle()}</h2>
            <p className="text-sm text-gray-600">{getStepDescription()}</p>
          </div>

          {/* Progress indicator - removed since we only have notification step */}

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start">
                <svg
                  className="w-5 h-5 text-red-500 mt-0.5 mr-2 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}

          {/* Retry message */}
          {retryCount > 0 && (
            <div className="mb-4 rounded-lg bg-yellow-50 p-3 text-sm text-yellow-700">
              <div className="flex items-center">
                <span className="mr-2">🔄</span>
                {t('retry_attempt', `Retry attempt ${retryCount}`)}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex space-x-3">
            <button
              onClick={handleRequestPermission}
              disabled={isRequesting}
              className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white py-3 rounded-xl font-medium transition-colors duration-200 flex items-center justify-center"
            >
              {isRequesting && (
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              )}
              {isRequesting ? t('requesting', 'Requesting...') : getButtonText()}
            </button>

            {currentStep === 'notification' && (
              <button
                onClick={handleDismiss}
                className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 py-3 rounded-xl font-medium transition-colors duration-200"
              >
                {t('maybe_later', 'Maybe Later')}
              </button>
            )}
          </div>

          {/* Help Text for Errors */}
          {error && retryCount > 1 && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start">
                <svg
                  className="w-5 h-5 text-blue-500 mt-0.5 mr-2 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
                <div className="text-sm text-blue-700">
                  <p className="font-medium mb-1">{t('notification_help_title', 'Need Help?')}</p>
                  <p>
                    {t(
                      'notification_help_text',
                      'You can manually enable notifications by clicking the lock icon in your browser address bar and selecting "Allow" for notifications.',
                    )}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Additional Info */}
          <div className="mt-4 text-center text-xs text-gray-500">
            {currentStep === 'notification' && (
              <p>
                {t('notification_info', 'You can change this setting anytime in your browser.')}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
